import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { User, Save, ShieldCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import api from '@/services/api';

const EMPTY = {
  full_name: '', username: '', avatar: '', bio: '', city: '', state: '',
  social_links: { instagram: '', twitter: '', website: '' },
};

export default function MyProfile() {
  const [form, setForm] = useState(EMPTY);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [authed, setAuthed] = useState(api.isAuthenticated());

  useEffect(() => {
    if (!api.isAuthenticated()) { setAuthed(false); setLoading(false); return; }
    api.get('/users/me')
      .then((me) => {
        setProfile(me);
        setForm({
          full_name: me.full_name || '',
          username: me.username || '',
          avatar: me.avatar || '',
          bio: me.bio || '',
          city: me.city || '',
          state: me.state || '',
          social_links: {
            instagram: me.social_links?.instagram || '',
            twitter: me.social_links?.twitter || '',
            website: me.social_links?.website || '',
          },
        });
      })
      .catch((e) => toast.error(e.message || 'Failed to load profile'))
      .finally(() => setLoading(false));
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setSocial = (k, v) => setForm((f) => ({ ...f, social_links: { ...f.social_links, [k]: v } }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, username: form.username.trim() || null };
      const updated = await api.put('/users/me', payload);
      setProfile(updated);
      toast.success('Profile updated');
    } catch (err) {
      toast.error(err.message || 'Update failed');
    } finally {
      setSaving(false);
    }
  };

  if (!authed) {
    return (
      <div className="max-w-xl mx-auto px-4 py-16 text-center">
        <User className="mx-auto mb-4 h-10 w-10 opacity-60" />
        <p className="mb-4">Please log in to view your profile.</p>
        <Link to="/login"><Button>Go to Login</Button></Link>
      </div>
    );
  }

  if (loading) return <div className="max-w-2xl mx-auto px-4 py-16 text-center opacity-70">Loading profile…</div>;

  return (
    <div className="max-w-2xl mx-auto px-4 py-8">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold flex items-center gap-2"><User className="h-6 w-6" /> My Profile</h1>
        {profile?.role && <Badge variant="secondary" className="capitalize">{profile.role === 'user' ? 'Attendee' : profile.role}</Badge>}
      </div>

      <Card>
        <CardHeader><CardTitle>Account</CardTitle></CardHeader>
        <CardContent>
          <form onSubmit={save} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-sm opacity-70">Email</Label>
                <Input value={profile?.email || ''} disabled />
              </div>
              <div className="flex items-end gap-2">
                {profile?.email_verified
                  ? <Badge className="bg-green-600"><ShieldCheck className="h-3 w-3 mr-1" /> Email verified</Badge>
                  : <Badge variant="outline">Email not verified</Badge>}
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="full_name">Full name</Label>
                <Input id="full_name" value={form.full_name} onChange={(e) => set('full_name', e.target.value)} />
              </div>
              <div>
                <Label htmlFor="username">Username</Label>
                <Input id="username" value={form.username} onChange={(e) => set('username', e.target.value)} placeholder="optional, unique" />
              </div>
            </div>
            <div>
              <Label htmlFor="avatar">Avatar URL</Label>
              <Input id="avatar" value={form.avatar} onChange={(e) => set('avatar', e.target.value)} placeholder="https://…" />
            </div>
            <div>
              <Label htmlFor="bio">Bio</Label>
              <Textarea id="bio" rows={3} value={form.bio} onChange={(e) => set('bio', e.target.value)} />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="city">City</Label>
                <Input id="city" value={form.city} onChange={(e) => set('city', e.target.value)} />
              </div>
              <div>
                <Label htmlFor="state">State</Label>
                <Input id="state" value={form.state} onChange={(e) => set('state', e.target.value)} />
              </div>
            </div>
            <div>
              <Label className="text-sm opacity-70">Social links</Label>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-1">
                <Input value={form.social_links.instagram} onChange={(e) => setSocial('instagram', e.target.value)} placeholder="Instagram" />
                <Input value={form.social_links.twitter} onChange={(e) => setSocial('twitter', e.target.value)} placeholder="Twitter / X" />
                <Input value={form.social_links.website} onChange={(e) => setSocial('website', e.target.value)} placeholder="Website" />
              </div>
            </div>
            <div className="flex items-center justify-between pt-2">
              <Link to="/organizer/profile" className="text-sm underline opacity-80">Organizer profile →</Link>
              <Button type="submit" disabled={saving}><Save className="h-4 w-4 mr-2" />{saving ? 'Saving…' : 'Save changes'}</Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
